import { supabase } from '@/integrations/supabase/client';

/**
 * Interface for PII data
 */
export interface PiiData {
  [key: string]: string | number | boolean | null;
  _chat_references?: ChatReferences;
  _enhanced?: boolean;
}

/**
 * Interface for chat references stored in PII data
 */
export interface ChatReferences {
  [chatbotId: string]: {
    last_updated: string;
    question_answers: Record<string, any>;
    session_count: number;
  };
}

/**
 * Interface for question answers
 */
export interface QuestionAnswers {
  [questionId: string]: string | number | boolean | null;
}

/**
 * Response from creating PII data
 */
export interface CreatePiiResponse {
  token: string;
  expires_at: string;
}

/**
 * Creates PII data and returns a token for accessing it
 * @param data The PII data to store
 * @param expiresInDays Number of days until the data expires (default: 30)
 * @returns A promise that resolves to the token response
 */
export async function createPiiData(
  data: PiiData,
  expiresInDays: number = 30
): Promise<CreatePiiResponse> {
  try {
    // Validate input data
    if (!data || typeof data !== 'object' || Object.keys(data).length === 0) {
      throw new Error('Invalid PII data: must be a non-empty object');
    }

    // Use the Supabase client to call the RPC function
    const { data: responseData, error } = await supabase.rpc('create_pii_data_with_token', {
      p_data: data,
      p_expires_days: expiresInDays
    });

    if (error) {
      console.error('Error creating PII data:', error);
      throw new Error(`Failed to create PII data: ${error.message}`);
    }

    if (!responseData) {
      throw new Error('No data returned from the server');
    }

    return responseData;
  } catch (error) {
    console.error('Error creating PII data:', error);
    throw error;
  }
}

/**
 * Retrieves PII data using a token
 * @param token The token to use for retrieving PII data
 * @param enhanced Whether to retrieve enhanced data with chat references
 * @returns A promise that resolves to the PII data
 */
export async function getPiiData(token: string, enhanced: boolean = false): Promise<PiiData> {
  try {
    // Validate token format
    if (!token || typeof token !== 'string') {
      throw new Error('Invalid PII token format');
    }

    if (enhanced) {
      // Use Supabase RPC to get enhanced PII data with chat references
      const { data: result, error } = await supabase.rpc('get_enhanced_pii_data_by_token', {
        p_token: token
      });

      if (error) {
        console.error('Error retrieving enhanced PII data:', error);
        return {};
      }

      if (!result) {
        console.warn('Enhanced PII token not found or expired');
        return {};
      }

      console.log('Enhanced PII data retrieved successfully:', {
        basicFields: Object.keys(result).filter(key => !key.startsWith('_')),
        hasChatReferences: !!result._chat_references,
        chatbotCount: result._chat_references ? Object.keys(result._chat_references).length : 0
      });

      return result;
    }

    try {
      const { data: rpcResult, error } = await supabase.rpc('get_pii_data_by_token', {
        p_token: token
      });

      if (error) {
        throw error;
      }

      if (!rpcResult) {
        console.warn('PII token not found or expired');
        return {};
      }

      console.log('PII data retrieved successfully via RPC:', Object.keys(rpcResult));
      return rpcResult;
    } catch (rpcError) {
      console.warn('Supabase RPC error, falling back to API endpoint:', rpcError);
    }

    if (typeof window === 'undefined') {
      console.warn('PII data fallback unavailable in non-browser environment');
      return {};
    }

    const baseUrl = window.location.origin;
    const response = await fetch(`${baseUrl}/api/pii?token=${encodeURIComponent(token)}`);

    if (!response.ok) {
      if (response.status === 404) {
        console.warn('PII token not found or expired');
        return {};
      }
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const payload = await response.json();
    const result = payload?.data ?? payload;

    if (!result || typeof result !== 'object') {
      console.warn('Invalid PII data format received from server');
      return {};
    }

    console.log('PII data retrieved successfully via API:', Object.keys(result));
    return result;
  } catch (error) {
    console.error('Error retrieving PII data:', error);
    // Return empty object instead of throwing to prevent breaking the chat flow
    return {};
  }
}

/**
 * Generates a URL with a PII token
 * @param baseUrl The base URL to append the token to
 * @param token The PII token
 * @returns The URL with the PII token appended
 */
export function generatePiiUrl(baseUrl: string, token: string): string {
  const url = new URL(baseUrl);
  url.searchParams.append('pii', token);
  return url.toString();
}

/**
 * Extracts a PII token from a URL
 * @param url The URL to extract the token from
 * @returns The PII token, or null if not found
 */
export function extractPiiToken(url: string): string | null {
  try {
    const parsedUrl = new URL(url);
    return parsedUrl.searchParams.get('pii');
  } catch (error) {
    console.error('Error extracting PII token:', error);
    return null;
  }
}

/**
 * Updates PII data with chat references and question answers
 * @param conversationId The conversation ID
 * @param questionAnswers The question answers to store
 * @param token The PII token
 * @returns A promise that resolves to the update result
 */
export async function updatePiiChatReferencesNew(
    conversationId: string,
    questionAnswers: QuestionAnswers,
    token: string
): Promise<{ success: boolean; error?: string }> {
  try {
    // Validate inputs
    if (!token || !questionAnswers) {
      throw new Error('Invalid parameters: token and questionAnswers are required');
    }

    console.log('🔄 Updating PII chat references:', {
      token: token.slice(0, 8) + '...',
      answerCount: Object.keys(questionAnswers).length
    });

    const flep_data = {};
    const fields = {};

    Object.entries(questionAnswers).forEach(([key, value]) => {
      if (key === 'first_name' || key === 'last_name' || key === 'email' || key === 'phone') {
        flep_data[key] = value
      } else {
        fields[key] = value
      }
    });

    console.log('🔄 Updating PII chat references !!!!!!!!!!!:', {
      flep_data,
      fields
    });

    // Use Supabase to update chat references
    const { data: result, error } = await supabase.functions.invoke('add-personality-field', {
      body: {
        flep_data,
        fields,
        piiToken: token,
        conversationId
      }
    });

    if (error) throw error;

    if (error) {
      console.error('Error updating PII chat references:', error);
      return { success: false, error: error.message };
    }

    if (result?.error) {
      console.error('PII update error:', result.error);
      return { success: false, error: result.error };
    }

    console.log('✅ PII chat references updated successfully:');

    return { success: true };
  } catch (error) {
    console.error('Error updating PII chat references:', error);
    return {
      success: false,
      error: error instanceof Error ? error.message : 'Unknown error'
    };
  }
}

/**
 * Get User information
 * @param conversationId The conversation ID
 * @param token The PII token
 * @returns User information
 */
export async function getPiiChatUserInformation(
    conversationId: string,
    token: string
): Promise<{ success: boolean; error?: string, data?: { fields: JSON } }> {
  try {
    // Validate inputs
    if (!token) {
      throw new Error('Invalid parameters: token are required');
    }

    console.log('🔄 Get PII user information:', {
      token: token.slice(0, 8) + '...',
    });

    // Use Supabase
    const { data: result, error } = await supabase.functions.invoke('get-personality-data', {
      body: {
        piiToken: token,
        conversationId
      }
    });

    if (error) throw error;

    if (error) {
      console.error('Error get PII user information:', error);
      return { success: false, error: error.message };
    }

    if (result?.error) {
      console.error('PII update error:', result.error);
      return { success: false, error: result.error };
    }

    console.log('✅ PII user information successfully:');

    return { success: true, data: result };
  } catch (error) {
    console.error('Error get PII user information:', error);
    return {
      success: false,
      error: error instanceof Error ? error.message : 'Unknown error'
    };
  }
}

/**
 * Updates PII data with chat references and question answers
 * @param token The PII token
 * @param chatbotId The chatbot ID
 * @param questionAnswers The question answers to store
 * @returns A promise that resolves to the update result
 */
export async function updatePiiChatReferences(
  token: string,
  chatbotId: string,
  questionAnswers: QuestionAnswers
): Promise<{ success: boolean; error?: string }> {
  try {
    // Validate inputs
    if (!token || !chatbotId || !questionAnswers) {
      throw new Error('Invalid parameters: token, chatbotId, and questionAnswers are required');
    }

    console.log('🔄 Updating PII chat references:', {
      token: token.slice(0, 8) + '...',
      chatbotId,
      answerCount: Object.keys(questionAnswers).length
    });

    // Use Supabase RPC to update chat references
    const { data: result, error } = await supabase.rpc('update_pii_chat_references', {
      p_token: token,
      p_chatbot_id: chatbotId,
      p_question_answers: questionAnswers
    });

    if (error) {
      console.error('Error updating PII chat references:', error);
      return { success: false, error: error.message };
    }

    if (result?.error) {
      console.error('PII update error:', result.error);
      return { success: false, error: result.error };
    }

    console.log('✅ PII chat references updated successfully:', {
      chatbotId,
      sessionCount: result?.updated_references?.[chatbotId]?.session_count
    });

    return { success: true };
  } catch (error) {
    console.error('Error updating PII chat references:', error);
    return {
      success: false,
      error: error instanceof Error ? error.message : 'Unknown error'
    };
  }
}

/**
 * Retrieves the auto-created PII token for a chat session
 * @param chatbotId The chatbot ID
 * @param sessionId The session ID
 * @returns The PII token if found, null otherwise
 */
export function getSessionPiiToken(chatbotId: string, sessionId: string): string | null {
  try {
    const token = sessionStorage.getItem(`pii_token_${chatbotId}_${sessionId}`);
    if (token) {
      console.log('📋 Retrieved session PII token:', {
        chatbotId,
        sessionId,
        tokenPreview: token.slice(0, 8) + '...'
      });
    }
    return token;
  } catch (error) {
    console.error('Failed to retrieve session PII token:', error);
    return null;
  }
}

/**
 * Creates a shareable URL with PII token for continuing a chat session
 * @param chatbotId The chatbot ID
 * @param sessionId The session ID (optional, uses current session if not provided)
 * @returns The shareable URL with PII token, or null if no token found
 */
export function createShareableChatUrl(chatbotId: string, sessionId?: string): string | null {
  try {
    // Use provided sessionId or try to get current session
    const targetSessionId = sessionId || getCurrentSessionId(chatbotId);
    if (!targetSessionId) {
      console.warn('No session ID available for creating shareable URL');
      return null;
    }

    const token = getSessionPiiToken(chatbotId, targetSessionId);
    if (!token) {
      console.warn('No PII token found for session:', { chatbotId, sessionId: targetSessionId });
      return null;
    }

    const baseUrl = window.location.origin;
    const shareableUrl = `${baseUrl}/chat/${chatbotId}?pii=${encodeURIComponent(token)}`;

    console.log('🔗 Created shareable chat URL:', {
      chatbotId,
      sessionId: targetSessionId,
      tokenPreview: token.slice(0, 8) + '...',
      url: shareableUrl
    });

    return shareableUrl;
  } catch (error) {
    console.error('Failed to create shareable chat URL:', error);
    return null;
  }
}

/**
 * Helper function to get current session ID from localStorage
 * @param chatbotId The chatbot ID
 * @returns The current session ID or null
 */
function getCurrentSessionId(chatbotId: string): string | null {
  try {
    return localStorage.getItem(`session_${chatbotId}`) || null;
  } catch (error) {
    console.error('Failed to get current session ID:', error);
    return null;
  }
}

/**
 * Retrieves previous answers from other chatbots for the same PII token
 * @param token The PII token
 * @param currentChatbotId The current chatbot ID (to exclude from results)
 * @param currentAnswers Current answers to merge with previous ones
 * @returns A promise that resolves to merged answers
 */
export async function getPreviousAnswers(
  token: string,
  currentChatbotId: string,
  currentAnswers: QuestionAnswers = {}
): Promise<QuestionAnswers> {
  console.log('getPreviousAnswers')
  try {
    // Validate inputs
    if (!token || !currentChatbotId) {
      console.warn('Invalid parameters for getPreviousAnswers');
      return currentAnswers;
    }

    console.log('🔍 Retrieving previous answers:', {
      token: token.slice(0, 8) + '...',
      currentChatbotId,
      currentAnswerCount: Object.keys(currentAnswers).length
    });

    // Use Supabase RPC to merge previous answers
    const { data: result, error } = await supabase.rpc('merge_previous_answers', {
      p_token: token,
      p_current_chatbot_id: currentChatbotId,
      p_current_answers: currentAnswers
    });

    if (error) {
      console.error('Error retrieving previous answers:', error);
      return currentAnswers;
    }

    if (!result) {
      console.log('No previous answers found, using current answers');
      return currentAnswers;
    }

    const mergedCount = Object.keys(result).length;
    const originalCount = Object.keys(currentAnswers).length;

    console.log('✅ Previous answers merged successfully:', {
      originalAnswers: originalCount,
      mergedAnswers: mergedCount,
      addedAnswers: mergedCount - originalCount
    });

    return result;
  } catch (error) {
    console.error('Error retrieving previous answers:', error);
    return currentAnswers;
  }
}

/**
 * Gets chat references for a specific chatbot or all chatbots
 * @param token The PII token
 * @param chatbotId Optional specific chatbot ID
 * @returns A promise that resolves to chat references
 */
export async function getChatReferences(
  token: string,
  chatbotId?: string
): Promise<ChatReferences | null> {
  try {
    // Validate token
    if (!token) {
      return null;
    }

    // Use Supabase RPC to get chat references
    const { data: result, error } = await supabase.rpc('get_pii_chat_references', {
      p_token: token,
      p_chatbot_id: chatbotId || null
    });

    if (error) {
      console.error('Error retrieving chat references:', error);
      return null;
    }

    if (!result || result.error) {
      console.warn('No chat references found or token expired');
      return null;
    }

    if (chatbotId) {
      // Return specific chatbot references
      return result.references ? { [chatbotId]: result.references } : null;
    } else {
      // Return all references
      return result.all_references || null;
    }
  } catch (error) {
    console.error('Error retrieving chat references:', error);
    return null;
  }
}

/**
 * Processes text with PII variables
 * @param text The text to process
 * @param piiData The PII data to use for variable replacement
 * @returns The processed text
 */
export function processPiiVariables(text: string, piiData: PiiData | null): string {
  if (typeof text !== 'string') {
    return text;
  }

  // Regex to find {{variableName}} / {{variableName:default}} and {variableName} / {variableName:default}
  return text.replace(/\{\{?\s*(\w+)(?:\s*:\s*([^}]+))?\s*\}\}?/g, (match, variableName, defaultValue) => {
    // Return the value from piiData if it exists, otherwise return the default value or the original match
    if (piiData && Object.prototype.hasOwnProperty.call(piiData, variableName)) {
      const value = String(piiData[variableName]);
      return value;
    } else if (defaultValue) {
      return defaultValue.trim();
    }
    return match;
  });
}
